/**
 * Minimal inline-SVG line chart — pure string building, no dependency.
 *
 * There is no chart library anywhere in this monorepo, and adding the first
 * runtime dependency to admin-ui for one page (then theming it by hand anyway)
 * is a worse trade than ~200 lines here. Shaped like the app's other pure
 * HTML-string helpers — skeleton.ts, empty-state.ts, pager.ts, icons.ts — and
 * lives beside them rather than in a directory of its own.
 *
 * Colour lives entirely in style.css against the existing theme custom
 * properties. The chart emits `class=` and never a hardcoded colour, so the
 * theme toggle keeps working; note that `var()` inside SVG PRESENTATION
 * attributes is not reliably supported across browsers, which is the other
 * reason classes rather than inline fills.
 */

export interface ChartPoint {
  x: number;
  y: number;
}

export interface ChartSeries {
  label: string;
  /** Ascending by x. */
  points: ChartPoint[];
  /** CSS class carrying the stroke colour, e.g. 'lc-line--mem'. */
  className: string;
  /** Also fill the area under the line, using `${className}-area`. */
  area?: boolean;
}

export interface ChartRefLine {
  y: number;
  label: string;
}

export type TickMode = 'binary' | 'decimal';

export interface LineChartOptions {
  series: ChartSeries[];
  refLines?: ChartRefLine[];
  formatY: (v: number) => string;
  formatX: (x: number) => string;
  /** Native <title> text per x-position. Omit to skip hover bands entirely. */
  tooltip?: (x: number, ys: ReadonlyArray<number | null>) => string;
  /** Byte axes want 1/2/4/8x2^n steps so labels read "256 MB", not "214.7 MB". */
  tickMode?: TickMode;
  yTickCount?: number;
  width?: number;
  height?: number;
  emptyMessage?: string;
  singlePointMessage?: string;
  ariaLabel?: string;
}

export interface Projection {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

// Byte labels like "1.02 GB" are wide, hence the generous left gutter.
const PAD_LEFT = 64;
const PAD_RIGHT = 14;
const PAD_TOP = 12;
const PAD_BOTTOM = 26;
const DEFAULT_WIDTH = 760;
const DEFAULT_HEIGHT = 240;
/**
 * Fold a reference line into the y-scale only when the DATA reaches at least
 * this fraction of it. With dev-shaped data (200 MB used against an 8 GB
 * limit) forcing the limit into the axis flattens the series into an
 * unreadable worm along the bottom of the frame. When the line is excluded the
 * legend says so rather than silently dropping it.
 */
const REF_LINE_SCALE_THRESHOLD = 0.35;
/** Above this, per-point hover bands stop being worth the markup. */
const MAX_HOVER_BANDS = 400;

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Axis ticks from 0 to at least `max`.
 *
 * Binary mode snaps the step to a power of two so byte labels land on 256 MB /
 * 512 MB / 1.00 GB rather than 214.7 MB. Decimal mode snaps to 1/2/2.5/5 x 10^n
 * for counts.
 */
export function niceTicks(max: number, count = 4, mode: TickMode = 'binary'): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const rough = max / count;
  let step: number;
  if (mode === 'binary') {
    step = 2 ** Math.ceil(Math.log2(rough));
  } else {
    const mag = 10 ** Math.floor(Math.log10(rough));
    const norm = rough / mag;
    const mult = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
    step = mult * mag;
  }
  const ticks: number[] = [];
  for (let v = 0; ticks.length === 0 || ticks[ticks.length - 1]! < max; v += step) {
    ticks.push(v);
    // Defensive: a pathological step must not spin forever.
    if (ticks.length > 64) break;
  }
  return ticks;
}

export function projectX(x: number, p: Projection): number {
  if (p.xMax === p.xMin) return p.left + p.width / 2;
  return p.left + ((x - p.xMin) / (p.xMax - p.xMin)) * p.width;
}

export function projectY(y: number, p: Projection): number {
  // y grows upward in the data and downward in SVG coordinates.
  if (p.yMax === p.yMin) return p.top + p.height / 2;
  return p.top + p.height - ((y - p.yMin) / (p.yMax - p.yMin)) * p.height;
}

const round2 = (n: number): string => (Math.round(n * 100) / 100).toString();

export function buildLinePath(points: ChartPoint[], p: Projection): string {
  if (points.length === 0) return '';
  return points
    .map((pt, i) => `${i === 0 ? 'M' : 'L'}${round2(projectX(pt.x, p))},${round2(projectY(pt.y, p))}`)
    .join(' ');
}

export function buildAreaPath(points: ChartPoint[], p: Projection): string {
  if (points.length < 2) return '';
  const line = buildLinePath(points, p);
  const x0 = round2(projectX(points[0]!.x, p));
  const xN = round2(projectX(points[points.length - 1]!.x, p));
  const base = round2(p.top + p.height);
  return `${line} L${xN},${base} L${x0},${base} Z`;
}

/** Frame + axes + an optional centred message. Shared by every render path so
 *  an empty chart occupies exactly the same box as a populated one. */
function frame(
  p: Projection,
  ticks: number[],
  formatY: (v: number) => string,
  width: number,
  height: number,
  message?: string,
): string {
  const grid = ticks
    .map((t) => {
      const y = round2(projectY(t, p));
      return (
        `<line class="lc-gridline" x1="${p.left}" y1="${y}" x2="${round2(p.left + p.width)}" y2="${y}" />` +
        `<text class="lc-ytick" x="${p.left - 8}" y="${y}" text-anchor="end" dominant-baseline="middle">${escapeHtml(formatY(t))}</text>`
      );
    })
    .join('');
  const msg = message
    ? `<text class="lc-empty" x="${round2(width / 2)}" y="${round2(height / 2)}" text-anchor="middle">${escapeHtml(message)}</text>`
    : '';
  return grid + msg;
}

export function lineChart(opts: LineChartOptions): string {
  const width = opts.width ?? DEFAULT_WIDTH;
  const height = opts.height ?? DEFAULT_HEIGHT;
  const tickCount = opts.yTickCount ?? 4;
  const mode = opts.tickMode ?? 'binary';
  const all = opts.series.flatMap((s) => s.points);

  const geom = {
    left: PAD_LEFT,
    top: PAD_TOP,
    width: width - PAD_LEFT - PAD_RIGHT,
    height: height - PAD_TOP - PAD_BOTTOM,
  };
  // preserveAspectRatio is "meet", not "none": stretching would distort every
  // stroke and glyph horizontally on a wide card. vector-effect keeps stroke
  // widths in device pixels regardless of the scale factor.
  const open =
    `<svg class="lc" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" ` +
    `role="img" aria-label="${escapeHtml(opts.ariaLabel ?? 'Time series chart')}">`;

  if (all.length === 0) {
    const p: Projection = { xMin: 0, xMax: 1, yMin: 0, yMax: 1, ...geom };
    const ticks = niceTicks(1, tickCount, mode);
    return (
      open +
      frame(p, ticks, opts.formatY, width, height, opts.emptyMessage ?? 'No data yet.') +
      '</svg>'
    );
  }

  const dataMax = Math.max(...all.map((pt) => pt.y));
  const refLines = opts.refLines ?? [];
  // Only reference lines the data comes close to participate in the scale — a
  // limit an order of magnitude above the series would squash it flat.
  const inScale = refLines.filter((r) => dataMax >= r.y * REF_LINE_SCALE_THRESHOLD);
  const offScale = refLines.filter((r) => dataMax < r.y * REF_LINE_SCALE_THRESHOLD);
  const scaleMax = Math.max(dataMax, ...inScale.map((r) => r.y));
  const ticks = niceTicks(scaleMax, tickCount, mode);

  const p: Projection = {
    xMin: Math.min(...all.map((pt) => pt.x)),
    xMax: Math.max(...all.map((pt) => pt.x)),
    // yMin is ALWAYS 0: a truncated axis exaggerates growth, and this chart
    // backs a capacity decision.
    yMin: 0,
    yMax: ticks[ticks.length - 1]!,
    ...geom,
  };

  const body: string[] = [frame(p, ticks, opts.formatY, width, height)];

  // A single sample cannot be a line. Draw the level and say so.
  const longest = Math.max(...opts.series.map((s) => s.points.length));
  if (longest < 2) {
    const only = opts.series.find((s) => s.points.length === 1)!.points[0]!;
    const y = round2(projectY(only.y, p));
    body.push(
      `<line class="lc-ref--single" x1="${p.left}" y1="${y}" x2="${round2(p.left + p.width)}" y2="${y}" />`,
      `<circle class="lc-dot" cx="${round2(projectX(only.x, p))}" cy="${y}" r="3" />`,
      `<text class="lc-empty" x="${round2(width / 2)}" y="${round2(height - PAD_BOTTOM - 8)}" text-anchor="middle">` +
        `${escapeHtml(opts.singlePointMessage ?? 'One sample so far.')}</text>`,
    );
    return open + body.join('') + '</svg>';
  }

  for (const r of inScale) {
    const y = round2(projectY(r.y, p));
    body.push(
      `<line class="lc-ref" x1="${p.left}" y1="${y}" x2="${round2(p.left + p.width)}" y2="${y}" />`,
      `<text class="lc-ref-label" x="${round2(p.left + p.width)}" y="${round2(Number(y) - 4)}" text-anchor="end">${escapeHtml(r.label)}</text>`,
    );
  }

  for (const s of opts.series) {
    if (s.points.length < 2) continue;
    if (s.area) {
      body.push(`<path class="lc-area ${escapeHtml(s.className)}-area" d="${buildAreaPath(s.points, p)}" />`);
    }
    body.push(
      `<path class="lc-line ${escapeHtml(s.className)}" vector-effect="non-scaling-stroke" fill="none" d="${buildLinePath(s.points, p)}" />`,
    );
  }

  // X labels at index fractions, taken from REAL sample timestamps so a label
  // always names a point that exists. First/last anchor inward so neither is
  // clipped at the frame edge.
  const ref = opts.series.reduce((a, b) => (b.points.length > a.points.length ? b : a)).points;
  for (const f of [0, 0.25, 0.5, 0.75, 1]) {
    const idx = Math.min(ref.length - 1, Math.round(f * (ref.length - 1)));
    const pt = ref[idx]!;
    const anchor = f === 0 ? 'start' : f === 1 ? 'end' : 'middle';
    body.push(
      `<text class="lc-xtick" x="${round2(projectX(pt.x, p))}" y="${height - 8}" text-anchor="${anchor}">${escapeHtml(opts.formatX(pt.x))}</text>`,
    );
  }

  // Hover bands: one transparent rect per x-position carrying a native
  // <title>. Zero JS, zero teardown, and no positioning code to get wrong.
  if (opts.tooltip && ref.length <= MAX_HOVER_BANDS) {
    const bandW = p.width / ref.length;
    ref.forEach((pt, i) => {
      const ys = opts.series.map((s) => s.points[i]?.y ?? null);
      body.push(
        `<rect class="lc-hit" x="${round2(p.left + i * bandW)}" y="${p.top}" width="${round2(bandW)}" height="${p.height}">` +
          `<title>${escapeHtml(opts.tooltip!(pt.x, ys))}</title></rect>`,
      );
    });
  }

  const legend = [
    ...opts.series
      .filter((s) => s.points.length > 0)
      .map(
        (s) =>
          `<span><span class="lc-legend-swatch ${escapeHtml(s.className)}"></span>${escapeHtml(s.label)}</span>`,
      ),
    // Say when a reference line was dropped, rather than silently omitting it.
    ...offScale.map((r) => `<span class="muted">${escapeHtml(r.label)} · off scale</span>`),
  ].join('');

  return `${open}${body.join('')}</svg><div class="lc-legend">${legend}</div>`;
}
